// LeaderboardPage — Top traders ranking
// Layout:
//   - Top nav: < Back | "Leaderboard"
//   - Hero: your rank
//   - Tab pills: All / This week / This month / All time
//   - Top 3 podium (gold/silver/bronze)
//   - Ranked list (4+)
//   - Each row: rank, avatar, name, tier badge, P&L, win rate

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, TierPill } from '../../components/Brand';
import { TIERS, type TierKey } from '../../data/platforms';

interface Trader {
  id: string;
  rank: number;
  name: string;
  handle: string;
  avatar: string;
  tier: TierKey;
  pnl: number;
  pnlPct: number;
  winRate: number;
  trades: number;
  badge?: 'verified' | 'pro' | 'rising';
}

const TRADERS: Trader[] = [
  { id: 't1', rank: 1, name: 'Marcus Chen',    handle: '@marcus',     avatar: 'user-1', tier: 'Professional', pnl: 24850, pnlPct: 248.5, winRate: 0.78, trades: 142, badge: 'verified' },
  { id: 't2', rank: 2, name: 'Sofia Petrov',   handle: '@sofia.p',    avatar: 'user-2', tier: 'Professional', pnl: 18420, pnlPct: 184.2, winRate: 0.74, trades: 128, badge: 'pro' },
  { id: 't3', rank: 3, name: 'Aiden Wright',   handle: '@aidenw',     avatar: 'user-3', tier: 'Professional', pnl: 14200, pnlPct: 142.0, winRate: 0.71, trades: 98,  badge: 'rising' },
  { id: 't4', rank: 4, name: 'Yuki Tanaka',    handle: '@yuki',       avatar: 'user-4', tier: 'Mid',          pnl:  9840, pnlPct:  98.4, winRate: 0.68, trades: 87 },
  { id: 't5', rank: 5, name: 'Brandon Lee',    handle: '@brandonl',   avatar: 'user-1', tier: 'Beginner',     pnl:  2920, pnlPct:  29.2, winRate: 0.67, trades: 119 },
  { id: 't6', rank: 6, name: 'Aisha Khan',     handle: '@aishak',     avatar: 'user-2', tier: 'Mid',          pnl:  8750, pnlPct:  87.5, winRate: 0.65, trades: 102 },
  { id: 't7', rank: 7, name: 'Diego Rivera',   handle: '@diegor',     avatar: 'user-3', tier: 'Professional', pnl: 11200, pnlPct: 112.0, winRate: 0.66, trades: 76 },
  { id: 't8', rank: 8, name: 'Lena Voss',      handle: '@lena',       avatar: 'user-4', tier: 'Mid',          pnl:  6420, pnlPct:  64.2, winRate: 0.62, trades: 89 },
  { id: 't9', rank: 9, name: 'Ravi Patel',     handle: '@ravip',      avatar: 'user-1', tier: 'Beginner',     pnl:  4180, pnlPct:  41.8, winRate: 0.60, trades: 71 },
  { id: 't10', rank: 10, name: 'Mia Johansson', handle: '@miaj',     avatar: 'user-2', tier: 'Mid',          pnl:  7820, pnlPct:  78.2, winRate: 0.63, trades: 94 },
];

const TABS = ['This week', 'This month', 'All time'] as const;
type Tab = typeof TABS[number];

export function LeaderboardPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('This month');
  const myRank = 5;
  const my = TRADERS.find((t) => t.id === 't5')!;
  const top3 = TRADERS.slice(0, 3);
  const rest = TRADERS.slice(3);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Leaderboard</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 4v12M5 12l7 7 7-7M4 20h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Hero — your rank */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center">
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Your rank · {tab}</div>
          <div className="mt-1 dynamic-large text-tf-text-1 tabular-nums">#{myRank}</div>
          <div className="mt-1 text-[13px] text-tf-text-3">of 2,847 traders</div>
          <div className="mt-3 flex items-center justify-center gap-3">
            <div className="text-center">
              <div className="text-[18px] font-bold tabular-nums text-tf-profit">+${my.pnl.toLocaleString()}</div>
              <div className="text-[11px] text-tf-text-3">P&L</div>
            </div>
            <div className="h-8 w-px bg-[#E5E5EA]" />
            <div className="text-center">
              <div className="text-[18px] font-bold tabular-nums text-tf-text-1">{(my.winRate * 100).toFixed(0)}%</div>
              <div className="text-[11px] text-tf-text-3">Win rate</div>
            </div>
            <div className="h-8 w-px bg-[#E5E5EA]" />
            <div className="text-center">
              <div className="text-[18px] font-bold tabular-nums text-tf-text-1">{my.trades}</div>
              <div className="text-[11px] text-tf-text-3">Trades</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab pills */}
      <div className="mx-4 mt-3 ios26-seg" data-testid="leaderboard-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={t === tab ? 'active' : ''}>
            {t}
          </button>
        ))}
      </div>

      {/* Top 3 podium */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Top 3</h2>
      <div className="mx-4 grid grid-cols-3 gap-2">
        {[
          { trader: top3[1], medal: '🥈', height: 'h-32' }, // 2nd (silver)
          { trader: top3[0], medal: '🥇', height: 'h-40' }, // 1st (gold, taller)
          { trader: top3[2], medal: '🥉', height: 'h-28' }, // 3rd (bronze)
        ].map(({ trader, medal, height }, i) => (
          <div key={trader.id} data-testid={`podium-${trader.rank}`} className="flex flex-col items-center">
            <div className="relative">
              <UserAvatar size={64} name={trader.name} />
              <div className="absolute -top-1 -right-1 text-[20px]">{medal}</div>
            </div>
            <div className="mt-2 text-center">
              <div className="text-[12px] font-bold text-tf-text-1 truncate w-full">{trader.name.split(' ')[0]}</div>
              <div className="text-[10px] text-tf-text-3 truncate w-full">{trader.handle}</div>
              <div className="mt-1 text-[12px] font-bold tabular-nums text-tf-profit">+${trader.pnl.toLocaleString()}</div>
            </div>
            <div className={'mt-1 w-full rounded-t-md bg-gradient-to-b from-yellow-300/20 to-transparent ' + height} />
          </div>
        ))}
      </div>

      {/* Rest of leaderboard */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">All traders</h2>
      <div className="mx-4 ios26-list bg-white">
        {rest.map((t, i) => (
          <div
            key={t.id}
            data-testid={`trader-${t.id}`}
            className={['flex items-center gap-3 px-4 py-3', i < rest.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
          >
            <div className="w-7 text-center text-[14px] font-bold tabular-nums text-tf-text-3">#{t.rank}</div>
            <UserAvatar size={40} name={t.name} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[14px] font-semibold text-tf-text-1 truncate">{t.name}</span>
                {t.badge === 'verified' && <span className="text-tf-blue text-[12px]">✓</span>}
                {t.badge === 'pro' && <span className="rounded bg-tf-text-1 px-1 text-[9px] font-bold uppercase text-white">PRO</span>}
                {t.badge === 'rising' && <span className="rounded bg-[#34C759] px-1 text-[9px] font-bold uppercase text-white">↑</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <TierPill tier={t.tier} />
                <span className="text-[11px] text-tf-text-3">{(t.winRate * 100).toFixed(0)}% · {t.trades} trades</span>
              </div>
            </div>
            <div className="text-right">
              <div className={['text-[14px] font-semibold tabular-nums', t.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                {t.pnl >= 0 ? '+' : ''}${t.pnl.toLocaleString()}
              </div>
              <div className={['text-[11px] tabular-nums', t.pnlPct >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                {t.pnlPct >= 0 ? '+' : ''}{t.pnlPct.toFixed(1)}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
